import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Copy, CheckCheck, Shield, Cpu, Eye, EyeOff,
  Calendar, Save, AlertCircle, CheckCircle, Users,
} from 'lucide-react';
import { usersAPI, systemAPI } from '../../services/api';
import { useRoleData } from '../../hooks/useRoleData';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import { useAppContext } from '../../context/AppContext';
import UsersTab from './UsersTab';
import './Settings.css';
import { formatDate } from '../../utils/dateFormatter';

const Settings = () => {
  const { showNotification, currentUser } = useAppContext();
  const { filteredUsers, loading: usersLoading, error: usersError, refreshData } = useRoleData();
  const [activeTab, setActiveTab] = usePersistentTab('tokens');
  const [tokens, setTokens] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  // System config state
  const [systemConfig, setSystemConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [configChanged, setConfigChanged] = useState(false);

  // Transform filteredUsers into tokens format
  useEffect(() => {
    if (filteredUsers.length > 0) {
      const newTokens = filteredUsers.map((user) => ({
        userId: user.id,
        userName: user.name,
        userRole: user.role,
        token: user.token || '',
        issuedDate: user.joinDate?.split('T')[0] || '2024-01-15',
        status: user.status === 'Active' ? 'Active' : 'Inactive',
        visible: false,
        userDepartment: user.department || '',
        userSpecialization: user.specialization || '',
        userInitials: user.initials || '',
      }));
      setTokens(newTokens);
    }
  }, [filteredUsers]);

  // Fetch system config
  const fetchSystemConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const response = await systemAPI.getConfig();
      const config = response.data?.data;
      if (config) {
        setSystemConfig({ ...config });
        setOriginalConfig({ ...config });
      }
    } catch (error) {
      console.error('Failed to load system config:', error);
      setConfigError(error.response?.data?.error || 'Failed to load system configuration');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'system') {
      fetchSystemConfig();
    }
  }, [activeTab, fetchSystemConfig]);

  // Track changes
  useEffect(() => {
    if (!systemConfig || !originalConfig) {
      setConfigChanged(false);
      return;
    }
    const changed =
      systemConfig.maxUploadSize !== originalConfig.maxUploadSize ||
      systemConfig.supportedPlatforms !== originalConfig.supportedPlatforms ||
      systemConfig.defaultLanguage !== originalConfig.defaultLanguage;
    setConfigChanged(changed);
  }, [systemConfig, originalConfig]);

  const handleReissue = async (userId) => {
    setGeneratingId(userId);
    try {
      await usersAPI.updateToken(userId);
      showNotification('Token re-issued successfully!', 'success');
      refreshData();
    } catch (error) {
      console.error('Failed to re-issue token:', error);
      showNotification(error.response?.data?.error || 'Failed to re-issue token', 'error');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleCopy = (token, userId) => {
    navigator.clipboard.writeText(token).catch(() => {});
    setCopiedId(userId);
    setTimeout(() => setCopiedId(null), 2000);
    showNotification('Token copied to clipboard', 'info');
  };

  const toggleVisibility = (userId) => {
    setTokens((prev) =>
      prev.map((t) => (t.userId === userId ? { ...t, visible: !t.visible } : t))
    );
  };

  const handleConfigChange = (field, value) => {
    setSystemConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      await systemAPI.updateConfig({
        maxUploadSize: systemConfig.maxUploadSize,
        supportedPlatforms: systemConfig.supportedPlatforms,
        defaultLanguage: systemConfig.defaultLanguage,
      });
      setOriginalConfig({ ...systemConfig });
      showNotification('System settings saved successfully!', 'success');
    } catch (error) {
      console.error('Failed to save system config:', error);
      showNotification(error.response?.data?.error || 'Failed to save system settings', 'error');
    } finally {
      setConfigSaving(false);
    }
  };

  const trainees = tokens.filter((t) => t.userRole === 'Trainee');
  const instructors = tokens.filter((t) => t.userRole === 'Instructor');

  const renderTokenSection = (title, users, badgeClass, deptField) => (
    <div className="card">
      <div className="card-header">
        <h3>{title}</h3>
        <span className={`badge ${badgeClass}`}>{users.length} users</span>
      </div>
      <div className="settings__token-table">
        <div className="settings__token-header">
          <span>User</span>
          <span>{deptField === 'userDepartment' ? 'Department / Role' : 'Specialization'}</span>
          <span>VR Token</span>
          <span>Issued Date</span>
          <span>Status</span>
          <span>Actions</span>
        </div>
        {users.length === 0 ? (
          <div className="settings__token-empty">
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>No {title.toLowerCase()} found.</p>
          </div>
        ) : (
          users.map((t) => (
            <div key={t.userId} className="settings__token-row">
              <div className="settings__token-user">
                <div
                  className="avatar"
                  style={{ width: 34, height: 34, fontSize: '0.7rem' }}
                >
                  {t.userInitials}
                </div>
                <span className="settings__token-name">{t.userName}</span>
              </div>
              <span className="settings__token-dept">
                {t[deptField] || '\u2014'}
              </span>
              <div className="settings__token-value">
                <div className="settings__token-display">
                  {t.visible ? (
                    <span className="settings__token-chars">{t.token}</span>
                  ) : (
                    <span className="settings__token-hidden">{'\u2022\u2022\u2022\u2022'}</span>
                  )}
                  <button
                    className="btn-icon"
                    onClick={() => toggleVisibility(t.userId)}
                    title={t.visible ? 'Hide token' : 'Show token'}
                  >
                    {t.visible ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                </div>
              </div>
              <span className="settings__token-date">
                <Calendar size={12} />
                {formatDate(t.issuedDate)}
              </span>
              <span className={`badge ${t.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                {t.status}
              </span>
              <div className="settings__token-actions">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleCopy(t.token, t.userId)}
                  title="Copy token"
                >
                  {copiedId === t.userId ? (
                    <CheckCheck size={13} style={{ color: 'var(--color-lime)' }} />
                  ) : (
                    <Copy size={13} />
                  )}
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => handleReissue(t.userId)}
                  title="Re-issue token"
                  disabled={generatingId === t.userId}
                >
                  <RefreshCw
                    size={13}
                    className={generatingId === t.userId ? 'settings__spin' : ''}
                  />
                  Re-issue
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="settings">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Settings</h1>
          <p>Manage VR access tokens and system preferences</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="settings__tabs">
        {[
          ...(currentUser?.role === 'Administrator' ? [{ id: 'users', label: 'Users', icon: Users }] : []),
          { id: 'tokens', label: 'VR Access Tokens', icon: Shield },
          { id: 'system', label: 'System', icon: Cpu },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`settings__tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && currentUser?.role === 'Administrator' && <UsersTab />}

      {/* Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className="settings__tokens animate-fade-in">
          {/* Info Banner */}
          <div className="settings__token-info">
            <div className="settings__token-info-icon">
              <Shield size={20} />
            </div>
            <div>
              <h4>VR Device Login Tokens</h4>
              <p>
                Each user is assigned a unique 4-character alphanumeric token to authenticate
                from their VR headset. Tokens are case-sensitive and expire only when re-issued.
              </p>
            </div>
          </div>

          {usersLoading && (
            <div style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>
              <RefreshCw size={20} className="settings__spin" style={{ margin: '0 auto' }} />
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>Loading tokens...</p>
            </div>
          )}
          {usersError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-4)', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', color: '#b91c1c' }}>
              <AlertCircle size={16} />
              <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>Error loading tokens: {usersError}</span>
              <button className="btn btn-outline btn-sm" onClick={refreshData}>Retry</button>
            </div>
          )}

          {!usersLoading && !usersError && (
            <>
              {/* Trainee Tokens - admin only */}
              {currentUser?.role === 'Administrator' && renderTokenSection('Trainee Tokens', trainees, 'badge-green', 'userDepartment')}

              {/* Instructor Tokens */}
              {renderTokenSection('Instructor Tokens', instructors, 'badge-blue', 'userSpecialization')}
            </>
          )}
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <div className="settings__system animate-fade-in">
          <div className="card">
            <div className="card-header">
              <h3>System Settings</h3>
              <p className="text-sm text-muted">Configure platform-wide preferences and limits</p>
            </div>
            <div className="card-body">
              {configLoading && (
                <div style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>
                  <RefreshCw size={20} className="settings__spin" style={{ margin: '0 auto' }} />
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>Loading system configuration...</p>
                </div>
              )}

              {configError && !configLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-4)', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', color: '#b91c1c' }}>
                  <AlertCircle size={16} />
                  <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>{configError}</span>
                  <button className="btn btn-outline btn-sm" onClick={fetchSystemConfig}>Retry</button>
                </div>
              )}

              {systemConfig && !configLoading && (
                <>
                  <div className="settings__system-grid">
                    {/* Platform Name (read-only) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Platform Name</span>
                      <div className="settings__system-value">
                        <span className="settings__system-text">{systemConfig.platformName}</span>
                      </div>
                    </div>

                    {/* Version (read-only) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Version</span>
                      <div className="settings__system-value">
                        <span className="settings__system-text">{systemConfig.version}</span>
                      </div>
                    </div>

                    {/* Max Upload Size (editable) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Max Content Upload Size</span>
                      <div className="settings__system-value">
                        <input
                          className="form-input"
                          value={systemConfig.maxUploadSize}
                          onChange={(e) => handleConfigChange('maxUploadSize', e.target.value)}
                          style={{ maxWidth: 220 }}
                        />
                      </div>
                    </div>

                    {/* Token Expiry (read-only) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Token Expiry</span>
                      <div className="settings__system-value">
                        <span className="settings__system-text">{systemConfig.tokenExpiry}</span>
                      </div>
                    </div>

                    {/* Supported Platforms (editable) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Supported Platforms</span>
                      <div className="settings__system-value">
                        <input
                          className="form-input"
                          value={systemConfig.supportedPlatforms}
                          onChange={(e) => handleConfigChange('supportedPlatforms', e.target.value)}
                          style={{ maxWidth: 320 }}
                        />
                      </div>
                    </div>

                    {/* Default Language (editable) */}
                    <div className="settings__system-row">
                      <span className="settings__system-label">Default Language</span>
                      <div className="settings__system-value">
                        <select
                          className="form-input"
                          value={systemConfig.defaultLanguage}
                          onChange={(e) => handleConfigChange('defaultLanguage', e.target.value)}
                          style={{ maxWidth: 220 }}
                        >
                          <option value="English">English</option>
                          <option value="French">French</option>
                          <option value="Spanish">Spanish</option>
                          <option value="Portuguese">Portuguese</option>
                          <option value="Arabic">Arabic</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="profile__form-actions" style={{ marginTop: 'var(--space-5)' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveConfig}
                      disabled={configSaving || !configChanged}
                    >
                      {configSaving ? (
                        <>
                          <RefreshCw size={15} className="settings__spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={15} />
                          Save System Settings
                        </>
                      )}
                    </button>
                    {configChanged && (
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', marginLeft: 'var(--space-3)' }}>
                        <CheckCircle size={12} style={{ display: 'inline', marginRight: 4, color: 'var(--color-amber)' }} />
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                </>
              )}

              {!systemConfig && !configLoading && !configError && (
                <div style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>No configuration data available.</p>
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 'var(--space-2)' }} onClick={fetchSystemConfig}>
                    Load Configuration
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
